/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getThirdPartyAxios } from '@/lib/axios/thirdPartyClient';
import {
  type GetStoreDetailsResponse,
  type GetStoreDetailsQueryParams,
  type YextStoreEntity,
} from '../interface/yext-types';
import { type GetStoreDetailsData } from '../interface/types';
import { useLocationsParams } from '../hooks/useLocationsParams';
import { type Locale } from '@/types/i18n';
import { getYextCountryCodes } from '../utils/getYextCountryCodes';
import { locationsPrefix } from '../constants/locationsPrefix';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';

type GetStoreDetailsOptions = {
  yextURL: string;
  yextAPIKey: string;
  yextVersion: string;
  locale: Locale;
  state: string;
  city: string;
  address: string;
};

const getStoreDetails = async (options: GetStoreDetailsOptions) => {
  const { yextURL, yextAPIKey, yextVersion, locale, state, city, address } = options;
  const missingYextParams: string[] = [];

  if (!yextURL) {
    missingYextParams.push('YEXT_URL');
  }
  if (!yextAPIKey) {
    missingYextParams.push('YEXT_API_KEY');
  }
  if (!yextVersion) {
    missingYextParams.push('YEXT_VERSION');
  }

  if (missingYextParams.length > 0) {
    throw new Error(`Missing Yext parameters in globalConfig: ${missingYextParams.join(', ')}`);
  }
  const locationsHome = locationsPrefix[locale];

  const localeSpecificFields =
    locale === countryCodes.mx
      ? ['c_mostradorProfesional', 'c_mostradorProfesional2']
      : locale === countryCodes.ptBr
        ? [
            'c_brazilTelephoneCommercial',
            'c_brazilWhatsappCommercial1',
            'c_brazilWhatsappCommercial2',
          ]
        : [];

  const params: GetStoreDetailsQueryParams = {
    entityType: 'location',
    limit: 1,
    fields: [
      'name',
      'geomodifier',
      'googlePlaceId',
      'address',
      'yextDisplayCoordinate',
      'mainPhone',
      'tollFreePhone',
      'hours',
      'timezone',
      'additionalHoursText',
      'closed',
      'websiteUrl',
      'c_autoPartsBody2',
      ...localeSpecificFields,
    ].join(','),
    filter: JSON.stringify({
      countryCode: { $in: getYextCountryCodes(locale) },
      'websiteUrl.url': {
        $contains: `/${locationsHome}/${encodeURIComponent(state)}/${encodeURIComponent(
          city,
        )}/${encodeURIComponent(address)}`,
      },
      closed: { $eq: false },
    }),
    v: yextVersion,
  };

  const response = await getThirdPartyAxios().get<GetStoreDetailsResponse>(
    yextURL + 'me/entities',
    {
      params,
      headers: {
        'api-key': yextAPIKey,
      },
    },
  );

  return selector(response.data);
};

const selector = (data: GetStoreDetailsResponse): GetStoreDetailsData | null => {
  const storeDetails = data.response.entities[0] as YextStoreEntity | undefined;

  if (storeDetails) {
    const {
      c_mostradorProfesional,
      c_mostradorProfesional2,
      c_brazilTelephoneCommercial,
      c_brazilWhatsappCommercial1,
      c_brazilWhatsappCommercial2,
    } = storeDetails;

    return {
      ...storeDetails,
      ...(c_mostradorProfesional && {
        c_mostradorProfesional: c_mostradorProfesional.replace(/[^0-9]/g, ''),
      }),
      ...(c_mostradorProfesional2 && {
        c_mostradorProfesional2: c_mostradorProfesional2.replace(/[^0-9]/g, ''),
      }),
      ...(c_brazilTelephoneCommercial && {
        c_brazilTelephoneCommercial: c_brazilTelephoneCommercial.replace(/[^0-9]/g, ''),
      }),
      ...(c_brazilWhatsappCommercial1 && {
        c_brazilWhatsappCommercial1: c_brazilWhatsappCommercial1.replace(/[^0-9]/g, ''),
      }),
      ...(c_brazilWhatsappCommercial2 && {
        c_brazilWhatsappCommercial2: c_brazilWhatsappCommercial2.replace(/[^0-9]/g, ''),
      }),
    };
  }
  return null;
};

const { useData, prefetch, getFromCache } = createQuery('storeDetails', getStoreDetails);

type UseStoreDetailsParams = {
  enabled?: boolean;
};

export const useStoreDetails = ({ enabled = true }: UseStoreDetailsParams = {}) => {
  const locale = useLocale();
  const { state = '', city = '', address = '' } = useLocationsParams();

  const yextURL = useFeatureFlag('YEXT_URL') ?? '';
  const yextAPIKey = useFeatureFlag('YEXT_API_KEY') ?? '';
  const yextVersion = useFeatureFlag('YEXT_VERSION') ?? '';

  return useData({
    yextURL,
    yextAPIKey,
    yextVersion,
    locale,
    state,
    city,
    address,
    enabled: Boolean(enabled && yextURL && yextAPIKey && yextVersion && state && city && address),
  });
};

export const prefetchStoreDetails = (
  queryClient: QueryClient,
  axios: AxiosInstance,
  options: GetStoreDetailsOptions,
) => {
  return prefetch(queryClient, options, axios);
};

export const getStoreDetailsFromCache = (
  queryClient: QueryClient,
  options: GetStoreDetailsOptions,
) => {
  return getFromCache(queryClient, options);
};
